import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bug, ChevronDown, ChevronsDownUp, ListTree, History, LogOut, Search, Wifi, Infinity as InfinityIcon, ShieldCheck, ShieldOff, Pin, MessageCircle, Headphones, X } from 'lucide-react';
import BrandMark from '../BrandMark';
import ResellerPresenceBadge from './ResellerPresenceBadge';
import BugReportModal from '../BugReportModal';
import SidebarUtilityButton from '../SidebarUtilityButton';
import ReleaseHistoryModal from './ReleaseHistoryModal';
import { useNavPins } from '../../hooks/useNavPins';
import { flattenNavItems, resolvePinnedItems } from '../../lib/navItems';
import { cn } from '../../lib/utils';
import { API_BASE_URL } from '../../apiBase';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { resolveUserDisplayName } from '../../lib/userDisplayName';
import SupportChoiceModal from './SupportChoiceModal';
import { supportContacts } from '../../lib/supportChannels';
import { usesModernOfficeVisual } from '../../lib/officeVisual';
import { navigationTone } from './officeNavigationPresentation';
import { PremiumSidebarIdentity, PremiumSidebarFooter } from './OfficePremiumSidebar';

const SectionLabel = ({ children }) => (
  <p className="office-nav-section-label px-3 pb-2 pt-5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
    {children}
  </p>
);

const SectionToggle = ({ children, open, onClick, controls }) => (
  <button
    type="button"
    className="office-nav-section-toggle flex w-full items-center justify-between px-3 pb-2 pt-5 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-white/40"
    aria-expanded={open}
    aria-controls={controls}
    onClick={onClick}
  >
    <span>{children}</span>
    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
  </button>
);

const NavItem = ({ item, badge, onNavigate, pinned, onTogglePin }) => {
  const { t } = useLanguage();
  const Icon = item.icon;
  return (
    <div className="office-nav-item group relative flex items-center" data-tone={navigationTone(item.path)} data-destaque2910={item.destaque2910 ? '1' : undefined} data-rec3010={item.rec3010 ? '1' : undefined}>
      <NavLink
        to={item.path}
        end={item.path === '/dashboard' || item.path === '/team' || item.path === '/triggers'}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'office-nav-link flex min-w-0 flex-1 items-center gap-3 rounded-[0.45rem] px-3 py-2.5 pr-9 text-sm font-medium transition-colors',
            isActive
              ? 'office-nav-link--active bg-primary/20 text-white shadow-[inset_3px_0_0_hsl(var(--primary))]'
              : 'text-white/65 hover:bg-white/[0.06] hover:text-white',
          )
        }
      >
        {({ isActive }) => (
          <>
            <span className="office-nav-icon" aria-hidden="true"><Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.7} /></span>
            <span className="office-nav-label">{item.label}</span>
            {item.rec3010 ? <span className="office-nav-rec3010" title="Recomendado para revenda">Recomendado</span> : null}{/* CONTEUDOS_3010 */}
            {badge > 0 ? (
              <span
                className={cn(
                  'office-nav-badge ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-[0.3rem] px-1.5 text-[11px] font-bold',
                  isActive ? 'bg-white/20 text-white' : 'bg-primary text-primary-foreground',
                )}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            ) : null}
          </>
        )}
      </NavLink>
      {onTogglePin ? (
        <button
          type="button"
          className={cn(
            'office-nav-pin absolute right-1 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-md transition-opacity focus-visible:opacity-100',
            pinned ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-100 text-white/40',
          )}
          title={pinned ? t('sidebar.unpin') : t('sidebar.pin')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin(item.path);
          }}
        >
          <Pin size={14} className={pinned ? 'fill-current' : ''} strokeWidth={pinned ? 2 : 1.75} />
        </button>
      ) : null}
    </div>
  );
};

/**
 * Sidebar sempre escura (paridade Office Cardoso) — independente do tema claro/escuro.
 * NÃO usa class "dark" aqui: isso redefinia --primary e ignorava a cor do tema.
 * O accent (--primary / --ring) herda do documentElement (ThemeContext).
 */
export default function OfficeSidebar({
  open,
  mobileSheet = false,
  visible = true,
  onClose,
  onCollapse,
  modules,
  badges = {},
  settings,
  user,
  accountSummary,
  onLogout,
  onOpenCommandPalette,
  userId,
}) {
  const modernVisual = usesModernOfficeVisual(user);
  const { pinnedPaths, togglePin } = useNavPins(userId);
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const visibleModules = useMemo(
    () =>
      (modules || [])
        .map((group) => ({
          ...group,
          items: (group.items || []).filter(
            (item) => !(user?.role === 'attendant' && item.path === '/gestor-xui/my-portfolio'),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [modules, user?.role],
  );
  const flat = flattenNavItems(visibleModules);
  const pinnedItems = resolvePinnedItems(pinnedPaths, flat);
  const [buildVersion, setBuildVersion] = useState('');
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [releaseHistoryOpen, setReleaseHistoryOpen] = useState(false);
  const [supportChoiceOpen, setSupportChoiceOpen] = useState(false);
  const collapseKey = `aurax:sidebar-groups:${userId || user?.id || 'user'}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(collapseKey) || 'null');
      if (saved && typeof saved === 'object') return saved;
    } catch { /* use professional defaults */ }
    return { creditos: true, iptv: true, admin: true, config: true };
  });

  useEffect(() => {
    const activeGroup = visibleModules.find((group) =>
      group.items.some((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)),
    );
    if (!activeGroup) return;
    setCollapsedGroups((current) => current[activeGroup.id] ? { ...current, [activeGroup.id]: false } : current);
  }, [location.pathname, visibleModules]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups((current) => {
      const next = { ...current, [groupId]: !current[groupId] };
      try { localStorage.setItem(collapseKey, JSON.stringify(next)); } catch { /* optional */ }
      return next;
    });
  };

  const anyGroupOpen = visibleModules.some(group => group.id !== 'home' && !collapsedGroups[group.id]);
  const toggleAllGroups = () => {
    setCollapsedGroups(current => {
      const next = { ...current };
      visibleModules.forEach(group => { if (group.id !== 'home') next[group.id] = anyGroupOpen; });
      try { localStorage.setItem(collapseKey, JSON.stringify(next)); } catch { /* optional presentation preference */ }
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/app/version`, { withCredentials: true });
        const raw = data?.build_version || data?.version || '';
        if (!cancelled && raw) {
          const v = String(raw).trim();
          setBuildVersion(v.toLowerCase().startsWith('v') ? v : `v${v}`);
        }
      } catch {
        /* opcional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const numberLocale = locale === 'en' ? 'en-US' : locale === 'es' ? 'es-ES' : 'pt-BR';
  const credits = accountSummary?.credits;
  const planLabel =
    user?.role === 'attendant'
      ? 'Perfil operacional'
      : credits?.mode === 'unlimited'
      ? t('sidebar.creditsUnlimited')
      : credits?.available != null
        ? t('sidebar.creditsCount', {
            n: Number(credits.available).toLocaleString(numberLocale),
          })
        : '—';

  const displayName = resolveUserDisplayName(user);
  const roleLabel =
    user?.role === 'admin'
      ? t('sidebar.role.admin')
      : user?.role === 'reseller'
        ? t('sidebar.role.reseller')
        : user?.role === 'attendant'
          ? t('sidebar.role.attendant')
          : t('sidebar.role.team');
  const showRoleBadge = user?.role === 'admin' && !/administrador|administrator/i.test(String(displayName));
  const mfaEnabled = Boolean(user?.mfa_enabled);
  const officeName = (settings?.brand_name || 'Dragon Tech').trim() || 'Dragon Tech';
  const buildLabel = buildVersion || '';
  const supportNumbers = supportContacts(settings);
  const supportPhone = supportNumbers.length === 1 ? `+${supportNumbers[0].phone}` : `${supportNumbers.length} números de suporte`;
  const supportLabel = String(settings?.support_label || 'Suporte').trim() || 'Suporte';
  const hasAtendimento = useMemo(
    () => flat.some((it) => it.path === '/chats'),
    [flat],
  );
  const showMasterSupport = user?.role === 'reseller' && supportNumbers.length > 0;

  const openSupport = () => {
    setSupportChoiceOpen(true);
  };

  return (
    <>
      {open && (
        <div
          className="office-sidebar-backdrop fixed inset-0 z-[45]"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        id="office-main-sidebar"
        role={open ? 'dialog' : undefined}
        aria-modal={open || undefined}
        aria-label={mobileSheet ? 'Menu completo' : 'Menu lateral'}
        aria-hidden={!visible}
        inert={!visible}
        className={cn(
          'office-sidebar fixed inset-y-0 left-0 z-[50] flex w-[min(272px,88vw)] max-w-[272px] transform flex-col border-r border-white/10 bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] transition-transform duration-300 lg:z-40 lg:w-[272px] lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
          modernVisual && 'office-sidebar--premium',
        )}
        data-testid="office-sidebar"
      >
        <div className="flex h-full min-h-0 flex-col pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]">
          {modernVisual ? <PremiumSidebarIdentity mobileSheet={mobileSheet} name={officeName} logoUrl={settings?.logo_url_dark || settings?.logo_url || settings?.logo_url_light || ''} displayName={displayName} roleLabel={roleLabel} onClose={onClose} onCollapse={onCollapse}>
            <div className="office-sidebar-account mt-3.5 min-w-0">
              <p className="truncate text-sm font-semibold text-white">{displayName}</p>
              <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-white/50">
                {credits?.mode === 'unlimited' ? (
                  <InfinityIcon className="h-3 w-3 shrink-0" aria-hidden />
                ) : (
                  <Wifi className="h-3 w-3 shrink-0" aria-hidden />
                )}{' '}
                <span className="truncate">{planLabel}</span>
              </p>
              <p
                className={cn(
                  'mt-0.5 flex min-w-0 items-center gap-1.5 text-xs',
                  mfaEnabled ? 'text-emerald-400/90' : 'text-white/45',
                )}
                title={
                  mfaEnabled
                    ? 'Google Authenticator ativo — código exigido no login'
                    : 'Google Authenticator desativado'
                }
                data-testid="sidebar-2fa-status"
              >
                {mfaEnabled ? (
                  <ShieldCheck className="h-3 w-3 shrink-0" />
                ) : (
                  <ShieldOff className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">
                  {mfaEnabled ? t('sidebar.mfaOn') : t('sidebar.mfaOff')}
                </span>
              </p>
              <ResellerPresenceBadge user={user} onNavigate={onClose} />
              {showRoleBadge ? (
                <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary/20 px-2 py-1 text-[11px] font-semibold text-primary">
                  <ShieldCheck className="h-3 w-3" /> {roleLabel}
                </span>
              ) : null}
            </div>
          </PremiumSidebarIdentity> : (
          <div className="office-sidebar-identity border-b border-white/10 px-4 py-4 sm:px-5">
            <button type="button" className="office-sidebar-close" aria-label="Fechar menu" onClick={onClose}><X size={18} /></button>
            <BrandMark
              name={settings?.brand_name || 'Dragon Tech'}
              logoUrl={settings?.logo_url_dark || settings?.logo_url || settings?.logo_url_light || ''}
              variant="square"
              size={40}
              logoMaxWidth={180}
              showName
              className="office-sidebar-brand min-w-0 justify-start overflow-hidden [&_.brand-name]:text-white"
            />
            <div className="office-sidebar-account mt-3.5 min-w-0">
              <p className="truncate text-sm font-semibold text-white">{displayName}</p>
              <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-white/50">
                {credits?.mode === 'unlimited' ? (
                  <InfinityIcon className="h-3 w-3 shrink-0" aria-hidden />
                ) : (
                  <Wifi className="h-3 w-3 shrink-0" aria-hidden />
                )}{' '}
                <span className="truncate">{planLabel}</span>
              </p>
              <p
                className={cn(
                  'mt-0.5 flex min-w-0 items-center gap-1.5 text-xs',
                  mfaEnabled ? 'text-emerald-400/90' : 'text-white/45',
                )}
                title={
                  mfaEnabled
                    ? 'Google Authenticator ativo — código exigido no login'
                    : 'Google Authenticator desativado'
                }
                data-testid="sidebar-2fa-status"
              >
                {mfaEnabled ? (
                  <ShieldCheck className="h-3 w-3 shrink-0" />
                ) : (
                  <ShieldOff className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">
                  {mfaEnabled ? t('sidebar.mfaOn') : t('sidebar.mfaOff')}
                </span>
              </p>
              <ResellerPresenceBadge user={user} onNavigate={onClose} />
              {showRoleBadge ? (
                <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary/20 px-2 py-1 text-[11px] font-semibold text-primary">
                  <ShieldCheck className="h-3 w-3" /> {roleLabel}
                </span>
              ) : null}
            </div>
          </div>
          )}

          <div className={cn("mt-3 px-3 sm:px-4", modernVisual && "premium-sidebar-search-row")}>
            <button
              type="button"
              onClick={() => {
                onOpenCommandPalette?.();
                onClose?.();
              }}
              className="office-sidebar-search flex w-full items-center gap-2 rounded-[0.35rem] border border-white/10 bg-white/5 px-3 py-2.5 text-left hover:bg-white/8"
            >
              <Search className="h-4 w-4 shrink-0 text-white/45" />
              <span className="min-w-0 flex-1 truncate text-sm text-white/45">{t('sidebar.search')}</span>
              <kbd className="hidden rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/50 sm:inline">
                Ctrl K
              </kbd>
            </button>
          </div>

          {visibleModules.some(group => group.id !== 'home' && !group.flat2810) && (
            <button type="button" className="office-nav-organize" onClick={toggleAllGroups}
              aria-label={anyGroupOpen ? 'Recolher seções do menu' : 'Expandir seções do menu'}
              data-testid="sidebar-organize-sections">
              {anyGroupOpen ? <ChevronsDownUp aria-hidden="true" /> : <ListTree aria-hidden="true" />}
              <span>{anyGroupOpen ? 'Recolher seções' : 'Expandir seções'}</span>
            </button>
          )}

          <nav aria-label="Menu principal" className="mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 sm:px-3 pb-6">
            {pinnedItems.length > 0 ? (
              <>
                <SectionLabel>{t('sidebar.favorites')}</SectionLabel>
                <div className="space-y-0.5">
                  {pinnedItems.map((it) => (
                    <NavItem
                      key={`pin-${it.path}`}
                      item={it}
                      badge={it.badgeKey ? badges[it.badgeKey] : 0}
                      onNavigate={onClose}
                      pinned
                      onTogglePin={togglePin}
                    />
                  ))}
                </div>
              </>
            ) : null}

            {visibleModules.map((mod) => {
              // MENU_ORDEM_2810: bloco plano = sem título, sem abrir/fechar, só um divisor fino
              const flat2810 = !!mod.flat2810;
              const canCollapse = mod.id !== 'home' && !flat2810;
              const groupOpen = !canCollapse || !collapsedGroups[mod.id];
              return (
              <div key={mod.id} className={cn('office-nav-group', flat2810 && 'office-nav-group--flat2810 mt-2 border-t border-white/10 pt-2 first:mt-0 first:border-t-0 first:pt-0')} data-collapsible={canCollapse} data-expanded={groupOpen}>
                {flat2810 ? null : canCollapse ? (
                  <SectionToggle open={groupOpen} controls={`office-nav-group-${mod.id}`} onClick={() => toggleGroup(mod.id)}>{mod.label}</SectionToggle>
                ) : (
                  <SectionLabel>{mod.label}</SectionLabel>
                )}
                <div id={`office-nav-group-${mod.id}`} className={cn('office-nav-group-items space-y-0.5', !groupOpen && 'hidden')}>
                  {mod.items.map((it) => (
                    <NavItem
                      key={it.path}
                      item={it}
                      badge={it.badgeKey ? badges[it.badgeKey] : 0}
                      onNavigate={onClose}
                      pinned={pinnedPaths.includes(it.path)}
                      onTogglePin={togglePin}
                    />
                  ))}
                </div>
              </div>
              );
            })}

            <div className="mt-4 space-y-0.5 border-t border-white/10 pt-3">
              <button
                type="button"
                onClick={onLogout}
                className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/55 transition-colors hover:bg-white/8 hover:text-white"
                data-testid="nav-logout"
              >
                <LogOut className="h-[18px] w-[18px]" />
                <span>{t('sidebar.logout')}</span>
              </button>
            </div>
          </nav>

          {modernVisual ? <PremiumSidebarFooter officeName={officeName} buildLabel={buildLabel} t={t} support={showMasterSupport ? (
                <button
                  type="button"
                  onClick={openSupport}
                  className="office-sidebar-support mb-2.5 flex w-full items-center gap-2.5 rounded-[0.35rem] border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-2 text-left transition hover:border-emerald-500/40 hover:bg-emerald-500/15"
                  data-testid="sidebar-master-support"
                >
                  <span className="office-sidebar-support__icon grid h-8 w-8 shrink-0 place-items-center rounded-[0.3rem] bg-emerald-500/20 text-emerald-400">
                    <Headphones className="h-4 w-4" />
                  </span>
                  <span className="office-sidebar-support__copy min-w-0 flex-1">
                    <span className="block truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-400/90">
                      {supportLabel}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1 truncate text-[11px] font-medium text-white/85">
                      <MessageCircle className="h-3 w-3 shrink-0 text-emerald-400" />
                      {supportPhone}
                    </span>
                  </span>
                </button>
            ) : null} onHistory={() => setReleaseHistoryOpen(true)} onReport={() => setBugReportOpen(true)} /> : (
          <div
            className="office-sidebar-footer shrink-0 border-t border-white/10 px-4 py-3 sm:px-5"
            data-testid="sidebar-office-meta"
          >
            {showMasterSupport ? (
                <button
                  type="button"
                  onClick={openSupport}
                  className="office-sidebar-support mb-2.5 flex w-full items-center gap-2.5 rounded-[0.35rem] border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-2 text-left transition hover:border-emerald-500/40 hover:bg-emerald-500/15"
                  data-testid="sidebar-master-support"
                >
                  <span className="office-sidebar-support__icon grid h-8 w-8 shrink-0 place-items-center rounded-[0.3rem] bg-emerald-500/20 text-emerald-400">
                    <Headphones className="h-4 w-4" />
                  </span>
                  <span className="office-sidebar-support__copy min-w-0 flex-1">
                    <span className="block truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-400/90">
                      {supportLabel}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1 truncate text-[11px] font-medium text-white/85">
                      <MessageCircle className="h-3 w-3 shrink-0 text-emerald-400" />
                      {supportPhone}
                    </span>
                  </span>
                </button>
            ) : null}
            <p className="truncate text-[11px] font-semibold tracking-wide text-white/70">
              <span className="text-white/90">{officeName}</span>
              {buildLabel ? (
                <>
                  <span className="text-white/35"> · </span>
                  <span className="tabular-nums text-white/80">{buildLabel}</span>
                </>
              ) : null}
            </p>
            <p className="mt-0.5 text-[10.5px] text-white/40">
              {t('sidebar.poweredBy')}{' '}
              <span className="font-semibold text-white/70">Dragon Tech</span>
            </p>
            <button
              type="button"
              onClick={() => setReleaseHistoryOpen(true)}
              className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-[0.35rem] border border-white/10 bg-white/5 px-2.5 py-2 text-[11px] font-semibold text-white/70 transition hover:border-primary/35 hover:bg-primary/15 hover:text-white"
              data-testid="sidebar-release-history"
            >
              <History className="h-3.5 w-3.5 shrink-0 text-primary" />
              {t('sidebar.releaseHistory')}
            </button>
            <SidebarUtilityButton icon={Bug} onClick={() => setBugReportOpen(true)} className="mt-1.5" data-testid="sidebar-report-bug">
              {t('sidebar.reportBug')}
            </SidebarUtilityButton>
          </div>
          )}
        </div>
      </aside>
      <SupportChoiceModal
        open={supportChoiceOpen}
        onOpenChange={setSupportChoiceOpen}
        supportLabel={supportLabel}
        hasAtendimento={hasAtendimento}
        onAtendimento={(conversation) => {
          navigate('/chats', { state: { supportConversation: conversation } });
          onClose?.();
        }}
      />
      <ReleaseHistoryModal
        open={releaseHistoryOpen}
        onOpenChange={setReleaseHistoryOpen}
      />
      <BugReportModal
        open={bugReportOpen}
        onOpenChange={setBugReportOpen}
        buildVersion={buildLabel}
      />
    </>
  );
}
